// src/components/CardCalendar.js
import React from "react";
import PropTypes from "prop-types";
import CardJubilee from "./CardJubilee";
import CardAgenda from "./CardAgenda";

/**
 * CardCalendar
 * Props:
 * - daysMap: { "YYYY-MM-DD": [event,...] }
 * - dayKeysOrdered: ["YYYY-MM-DD", ...]
 * - lunarMap: optional map by date
 * - loading: bool
 * - headerMonth: Date
 * - onOpenDetail: fn(event)
 * - prevMonth, nextMonth: fn
 * - listRef, sentinelRef: refs optional
 */

const css = `
.card-calendar{display:flex;flex-direction:column;gap:0.5rem}
.calendar-scroll-area{display:flex;flex-direction:column;gap:0.75rem;max-height:68vh;overflow:auto;padding:0.5rem 0.25rem}
.agenda-day-row{display:flex;gap:0.75rem;padding:0.625rem;background:#fff;border-radius:0.5rem;align-items:flex-start;box-shadow:0 0.1875rem 0.625rem rgba(10,10,10,0.03)}
.agenda-day-row.past{background:#f6f7f8}
.agenda-day-row.past .events-list{opacity:0.92}
.agenda-day-row.past .day-title,.agenda-day-row.past .day-subtitle{color:#3b3b3b}
.day-badge{width:4.75rem;flex:0 0 4.75rem;text-align:center;padding:0.5rem;border-radius:0.5rem;border:1px solid #eef2f7}
.badge-day{font-size:1.375rem;font-weight:800;color:#2b4a84}
.badge-weekday{font-size:0.75rem;color:#666;margin-top:0.25rem}
.day-header .day-title{font-size:0.9375rem;font-weight:700}
.day-subtitle{font-weight:700}
.day-badge.sat .badge-day,.day-header.sat .day-title{color:#f76809}
.day-badge.sun .badge-day,.day-header.sun .day-title{color:#cf282b}
.day-badge.lunar-special{background:linear-gradient(#fffbe5,#fffdf5);border-color:#ffefb3}
.area-badge.area-cat1{background:#e8f0ff;color:#133b8a}
.area-badge.area-cat2{background:#efe8ff;color:#4b2f6b}
.area-badge.area-cat3{background:#e6f7e9;color:#146b2e}
`;

/* Helpers */
function isoYmdFromTs(ts) {
  if (!ts) return null;
  return String(ts).split("T")[0];
}

function weekendType(dateKey) {
  const dt = new Date(dateKey + "T00:00:00Z");
  const day = isNaN(dt.getUTCDay()) ? new Date(dateKey + "T00:00:00").getDay() : dt.getUTCDay();
  if (day === 6) return "sat";
  if (day === 0) return "sun";
  return null;
}

function getLunarForDate(lunarMap, dateKey) {
  return lunarMap?.[dateKey] || null;
}

function formatMonthYear(date, locale = "en-US") {
  return date.toLocaleDateString(locale, {
    month: "long",
    year: "numeric",
  });
}

export default function CardCalendar({
  daysMap = {},
  dayKeysOrdered = [],
  lunarMap = {},
  loading = false,
  headerMonth = new Date(),
  onOpenDetail,
  prevMonth,
  nextMonth,
  listRef,
  sentinelRef,
  setDayRef,
  debugScroll = false,
  onLoadPrevMonth,
}) {
  let prevMonthLabel = "Load bulan sebelum";

  if (dayKeysOrdered && dayKeysOrdered.length > 0) {
    const firstKey = dayKeysOrdered[0]; // paling atas
    const firstDate = new Date(firstKey + "T00:00:00Z");

    const prevMonthDate = new Date(
      firstDate.getUTCFullYear(),
      firstDate.getUTCMonth() - 1,
      1
    );

    prevMonthLabel = `[ Load ${formatMonthYear(prevMonthDate, "en-US")} ]`;
    // kalau mau Indo:
    // prevMonthLabel = `[ Load ${formatMonthYear(prevMonthDate, "id-ID")} ]`;
  }

  function renderDayRow(dateKey) {
    const events = daysMap[dateKey] || [];
    const dt = new Date(dateKey + "T00:00:00Z");
    const dayNum = dt.getUTCDate();
    const indoDays = ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"];
    const weekdayShort = indoDays[dt.getUTCDay()];
    //const weekdayShort = dt.toLocaleDateString(undefined, { weekday: "short" });
    const wk = weekendType(dateKey);
    const lunar = getLunarForDate(lunarMap, dateKey);
    const lunarSpecial = lunar && !lunar.leapMonth && (lunar.lunarDay === 1 || lunar.lunarDay === 15);
    const todayKey = new Date().toISOString().slice(0, 10);
    const isPast = dateKey < todayKey;

    return (
      <div
        key={dateKey}
        ref={node => {
          if (setDayRef) setDayRef(dateKey, node);
          if (debugScroll && node) node.style.outline = "2px dashed red";
        }}
        data-daykey={dateKey}
        className={"agenda-day-row" + (isPast ? " past" : "")}
      >
        <div
          className={"day-badge " + (wk ? wk + " " : "") + (lunarSpecial ? " lunar-special" : "")}
          data-daykey={dateKey}
          style={{}}
        >
          <div className={"badge-day " + (wk ? wk : "")}>{String(dayNum).padStart(2, "0")}</div>
          <div className="badge-weekday">{weekdayShort}</div>
          {lunar && !lunar.leapMonth ? (
            <div style={{ fontSize: "0.6875rem", color: "#555", marginTop: "0.375rem" }}>
              {lunar.lunarMonth}月{lunar.lunarDay}日
            </div>
          ) : null}
        </div>

        <div style={{ flex: 1 }}>
          <div className={"day-header " + (wk ? wk : "")} style={{ marginBottom: "0.25rem" }}>
            <div className="day-title">
              {
                `${indoDays[dt.getUTCDay()]} ${dt.getUTCDate()} ${dt.toLocaleDateString("id-ID", { month: "long", year: "numeric" })}`
                //dt.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" })
              }
            </div>
            <div className="day-subtitle" style={{ fontSize: "0.8125rem", color: "#666" }}>
              歲次{lunar?.lunarGanZhi ?? ""} {lunar?.leapMonth ? "閏" : ""}{lunar?.lunarMonth ?? ""}月{lunar?.lunarDay ?? ""}日
            </div>
          </div>

          <div className="events-list">
            {events.map((ev) =>
              ev.jubilee ? <CardJubilee key={ev.id || ev.documentId || JSON.stringify(ev)} jub={ev} /> : (
                <CardAgenda key={ev.id || ev.documentId || JSON.stringify(ev)} ev={ev} onOpen={onOpenDetail} />
              )
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card-calendar">
      <style dangerouslySetInnerHTML={{ __html: css }} />
      <div className="calendar-scroll-area" ref={listRef}>
        {typeof onLoadPrevMonth === "function" && !loading && (
          <div
            style={{
              padding: "0.5rem",
              textAlign: "center",
              fontSize: "0.875rem",
              color: "#2b4a84",
              cursor: "pointer",
              fontWeight: 700,
              borderBottom: "1px dashed #dde5f2"
            }}
            onClick={onLoadPrevMonth}
          >
            {prevMonthLabel}
          </div>
        )}

        {dayKeysOrdered.length === 0 && !loading ? (
          <div style={{ padding: "1.875rem", textAlign: "center", color: "#666" }}>No results</div>
        ) : (
          (() => {
            const nodes = [];
            let lastMonthKey = null;
            const activeMonthKey = `${headerMonth.getFullYear()}-${String(headerMonth.getMonth() + 1).padStart(2, "0")}`;
            dayKeysOrdered.forEach((k) => {
              const dt = new Date(k + "T00:00:00Z");
              const monthKey = `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}`;
              if (monthKey !== lastMonthKey) {
                nodes.push(
                  <div className="month-sep" key={`sep-${monthKey}`} style={{ padding: "0.75rem 0.625rem", background: "#f7fafc", borderRadius: "0.5rem", margin: "0.75rem 0 0.25rem 0", fontWeight: 700, color: "#2b4a84", position: "sticky", top: "0.5rem", zIndex: 20 }}>
                    <div style={{ position: "relative", width: "100%", textAlign: "center" }}>
                      {monthKey === activeMonthKey && (
                        <>
                          <button aria-label="Prev month" onClick={prevMonth} style={{ position: "absolute", left: "0.5rem", top: "50%", transform: "translateY(-50%)", background: "transparent", border: "1px solid rgba(43,74,132,0.12)", padding: "0.375rem 0.5rem", borderRadius: "0.375rem", cursor: "pointer", fontWeight: 700, display:'none' }}>◀</button>
                          <button aria-label="Next month" onClick={nextMonth} style={{ position: "absolute", right: "0.5rem", top: "50%", transform: "translateY(-50%)", background: "transparent", border: "1px solid rgba(43,74,132,0.12)", padding: "0.375rem 0.5rem", borderRadius: "0.375rem", cursor: "pointer", fontWeight: 700, display:'none' }}>▶</button>
                        </>
                      )}
                      {new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), 1)).toLocaleDateString(undefined, { year: "numeric", month: "long" })}
                    </div>
                  </div>
                );
                lastMonthKey = monthKey;
              }
              nodes.push(renderDayRow(k));
            });
            return nodes;
          })()
        )}

        <div ref={sentinelRef} style={{ height: 2 }} />
        {loading && <div style={{ padding: "0.75rem", textAlign: "center" }}>Loading…</div>}
      </div>
    </div>
  );
}

CardCalendar.propTypes = {
  daysMap: PropTypes.object,
  dayKeysOrdered: PropTypes.array,
  lunarMap: PropTypes.object,
  loading: PropTypes.bool,
  headerMonth: PropTypes.instanceOf(Date),
  onOpenDetail: PropTypes.func,
  prevMonth: PropTypes.func,
  nextMonth: PropTypes.func,
  listRef: PropTypes.object,
  sentinelRef: PropTypes.object,
};
